import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';

const API_ROOT = 'https://api.okendo.io/v1';

// Okendo's own widget records the visitor's helpful votes under this key;
// sharing it keeps votes cast here and in the full widget in sync.
const VOTE_STORAGE_KEY = 'okeReviews-widget-votes';

// Okendo's hosted review capture ("recorder plus") — the same URL the OOB
// widget's "Write a Review" button opens (TS has recorderPlusEnabled).
const RECORDER_ROOT = 'https://okendo.reviews';

/**
 * Hydrates the TS Okendo Review Highlights section from Okendo's public
 * Storefront REST API (no auth; see https://docs.okendo.io):
 *
 * - `/review_aggregate` → rating distribution bars + recommendation
 *   consensus (and the average, when the metafield didn't provide one).
 * - `/reviews`          → the featured review cards.
 * - `/reviews_summary`  → the AI-generated summary box (empty object until
 *   Okendo has generated and published one).
 *
 * Each request hydrates independently; on failure its region simply stays
 * hidden, leaving the server-rendered aggregate intact.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} average - Average rating figure (may be server-rendered).
 * @property {HTMLOListElement} distribution - Distribution bars container.
 * @property {HTMLElement} summary - AI summary box.
 * @property {HTMLElement} summaryBody - AI summary text.
 * @property {HTMLElement} consensus - "N% recommend" line.
 * @property {HTMLUListElement} attributes - Attribute consensus lines (e.g. Sizing).
 * @property {HTMLElement} cards - Wrapper around the review-cards carousel (slides live in its slideshow-slides).
 * @property {HTMLTemplateElement} [carouselTemplate] - Inert carousel shell, swapped in once the slides exist.
 * @property {HTMLTemplateElement} [starTemplate] - Star SVG cloned into the modal items' rating rows.
 * @property {HTMLTemplateElement} [likeTemplate] - Like icon cloned into the modal items' vote buttons (CSS flips the dislike copy).
 * @property {HTMLTemplateElement} [likeFilledTemplate] - Filled like icon, shown in place of the outline on the visitor's own vote.
 * @property {HTMLTemplateElement} [verifiedTemplate] - Checkmark cloned into the Verified Buyer badge.
 *
 * A per-review sizing attribute (also the aggregate's shape).
 *
 * @typedef {object} OkendoAttributeRating
 * @property {string} [title]
 * @property {string} [type]
 * @property {number} [value]
 * @property {string} [minLabel]
 * @property {string} [midLabel]
 * @property {string} [maxLabel]
 *
 * @typedef {object} OkendoReview
 * @property {string} [reviewId]
 * @property {string} [title]
 * @property {string} [body]
 * @property {number} [rating]
 * @property {string} [dateCreated]
 * @property {string} [productVariantName]
 * @property {Array<{title?: string, type?: string, value?: string | string[]}>} [productAttributes]
 * @property {string} [productImageUrl]
 * @property {string} [productUrl]
 * @property {string} [productName]
 * @property {number} [helpfulCount]
 * @property {number} [unhelpfulCount]
 * @property {{displayName?: string, isVerified?: boolean, attributes?: Array<{title?: string, type?: string, value?: string}>}} [reviewer]
 * @property {OkendoAttributeRating[]} [attributesWithRating]
 * @property {Array<{type?: string, thumbnailUrl?: string, alt?: string}>} [media]
 *
 * @typedef {object} OkendoAnswer
 * @property {string} [answerId]
 * @property {string} [body] - HTML.
 * @property {string} [dateCreated]
 * @property {string} [status]
 * @property {boolean} [isPrivate]
 * @property {number} [helpfulCount]
 * @property {number} [unhelpfulCount]
 * @property {{displayName?: string}} [author]
 *
 * @typedef {object} OkendoQuestion
 * @property {string} [questionId]
 * @property {string} [body]
 * @property {string} [dateCreated]
 * @property {number} [helpfulCount]
 * @property {number} [unhelpfulCount]
 * @property {{displayName?: string}} [author]
 * @property {OkendoAnswer[]} [answers]
 *
 * One card body's clamp state (see #truncateCardBody).
 *
 * @typedef {object} CardClamp
 * @property {HTMLParagraphElement} body - Clamp container.
 * @property {HTMLSpanElement} text - Span holding the (possibly trimmed) review text.
 * @property {HTMLButtonElement} toggle - Inline "Read more / Read less" button.
 * @property {string} full - The untrimmed review body.
 * @property {number} lastWidth - Last truncated-at width (re-trim on width changes only).
 *
 * @extends {Component<Refs>}
 */
class TSOkendoReviewHighlights extends Component {
  requiredRefs = ['average', 'distribution', 'summary', 'summaryBody', 'consensus', 'attributes', 'cards'];

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {ResizeObserver | null} */
  #cardResizeObserver = null;

  /** @type {CardClamp[]} */
  #cardClamps = [];

  /** @type {Set<CardClamp>} */
  #pendingClamps = new Set();

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #retrimTimer = undefined;

  /** @type {Intl.RelativeTimeFormat | null} */
  #relativeTimeFormat = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    this.#hydrate();
    // The modal's inner controls sit inside the dialog-component's scope, so
    // `on:click` can't route to this component — wire them by class instead,
    // scoped to the connect lifetime so a reconnect can't stack listeners.
    const { signal } = this.#abortController;
    this.querySelector('.ts-okendo-review-highlights__see-all')?.addEventListener('click', this.#openReviewsModal, {
      signal,
    });
    for (const button of this.querySelectorAll('.ts-okendo-reviews-modal__more')) {
      button.addEventListener('click', this.#loadNextModalPage, { signal });
    }
    for (const select of this.querySelectorAll('.ts-okendo-reviews-modal__sort-select')) {
      select.addEventListener('change', this.#changeModalSort, { signal });
    }
    for (const tab of this.querySelectorAll('.ts-okendo-reviews-modal__tab')) {
      tab.addEventListener('click', this.#selectClickedTab, { signal });
    }
    this.querySelector('.ts-okendo-reviews-modal__tabs')?.addEventListener('keydown', this.#handleTabKeydown, {
      signal,
    });
    this.#setUpRecorderLinks();
    this.#resetModalTransientState();
  }

  /**
   * Pagination state lives in data attributes on each panel's list (see
   * #loadModalPage) so it survives section morphs/clones — but transient
   * in-flight markers cloned mid-request (aria-busy, hidden controls) would
   * deadlock a fresh instance, which has no in-flight requests. Reset them.
   */
  #resetModalTransientState() {
    for (const panel of this.querySelectorAll('.ts-okendo-reviews-modal__panel')) {
      const list = panel.querySelector('.ts-okendo-reviews-modal__list');
      if (!(list instanceof HTMLElement)) continue;
      list.setAttribute('aria-busy', 'false');
      list.style.minHeight = '';
      // Skeletons cloned mid-flight have no request left to clear them.
      for (const skeleton of list.querySelectorAll('.ts-okendo-reviews-modal__skeleton')) skeleton.remove();
      const more = panel.querySelector('.ts-okendo-reviews-modal__more');
      if (more instanceof HTMLElement) more.hidden = !list.dataset.nextUrl;
    }
  }

  /**
   * Points "Write a review" and "Ask a question" at Okendo's hosted
   * recorder — the same links the OOB widget's buttons open (new tab).
   */
  #setUpRecorderLinks() {
    this.#setUpRecorderLink('.ts-okendo-reviews-modal__write', '', 'reviews-widget-write-review-button');
    this.#setUpRecorderLink('.ts-okendo-reviews-modal__ask', 'question', 'questions-widget-ask-question-button');
  }

  /**
   * The server renders the bare recorder URL; this appends the subscriber
   * and product params. Without a resolvable subscriber id the recorder
   * can't work, so the CTA hides instead of dangling.
   *
   * @param {string} selector - The CTA anchor inside the modal.
   * @param {string} path - Recorder path: '' = review capture, 'question' = ask a question.
   * @param {string} channel - okeChannel tag (mirrors the OOB widget's buttons).
   */
  #setUpRecorderLink(selector, path, channel) {
    const link = this.querySelector(selector);
    if (!(link instanceof HTMLAnchorElement)) return;

    const subscriberId = this.dataset.subscriberId || this.#embedSubscriberId;
    if (!subscriberId) {
      link.hidden = true;
      return;
    }
    link.hidden = false;

    const url = new URL(`${RECORDER_ROOT}/${path}`);
    url.searchParams.set('subscriberId', subscriberId);
    url.searchParams.set('productId', `shopify-${this.dataset.productId}`);
    const locale = document.documentElement.lang;
    if (locale) url.searchParams.set('locale', locale);
    url.searchParams.set('okeChannel', channel);

    link.href = url.href;
    link.target = '_blank';
    link.rel = 'noopener';
    if (this.dataset.stringNewWindow && !link.querySelector('.visually-hidden')) {
      const hint = document.createElement('span');
      hint.className = 'visually-hidden';
      hint.textContent = ` (${this.dataset.stringNewWindow})`;
      link.append(hint);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
    this.#cardResizeObserver?.disconnect();
    this.#cardResizeObserver = null;
    clearTimeout(this.#retrimTimer);
    this.#pendingClamps.clear();
  }

  get #storeBase() {
    const subscriberId = this.dataset.subscriberId || this.#embedSubscriberId;
    if (!subscriberId) return null;
    return `${API_ROOT}/stores/${subscriberId}`;
  }

  get #apiBase() {
    return this.#storeBase && `${this.#storeBase}/products/shopify-${this.dataset.productId}`;
  }

  /**
   * Fallback when the `okendo.SubscriberId` shop metafield is unset: the
   * Okendo app embed injects its settings as JSON into every page.
   *
   * @returns {string | null}
   */
  get #embedSubscriberId() {
    try {
      const settings = document.getElementById('oke-reviews-settings');
      if (settings?.textContent) {
        const parsed = JSON.parse(settings.textContent);
        if (parsed?.subscriberId) return parsed.subscriberId;
      }
    } catch {
      // Malformed embed settings — fall through to the meta tag.
    }
    const meta = document.querySelector('meta[name="oke:subscriber_id"]');
    return meta?.getAttribute('content') || null;
  }

  /**
   * @param {string} path - Endpoint path (with query) appended to the product
   *   base, or a root-relative API path (`/stores/…` — the paginated
   *   `nextUrl` comes back in that form).
   * @returns {Promise<any | null>} Parsed JSON, or null when unavailable.
   */
  async #fetchJson(path) {
    const url = path.startsWith('/') ? `${API_ROOT}${path}` : this.#apiBase && `${this.#apiBase}/${path}`;
    if (!url) return null;

    try {
      // No `okendo-api-version` header: the API's CORS preflight doesn't
      // allow it, so browsers reject the request when it's present.
      const response = await fetch(url, {
        signal: this.#abortController?.signal,
      });
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }

  #hydrate() {
    this.#hydrateAggregate();
    this.#hydrateCards();
    this.#hydrateQuestionCount();
    if (!this.hasAttribute('data-hide-summary')) this.#hydrateSummary();
  }

  /**
   * Reveals the modal's Questions tab (with its count) when the product has
   * approved questions.
   */
  async #hydrateQuestionCount() {
    const tab = this.querySelector('.ts-okendo-reviews-modal__tab[data-tab="questions"]');
    if (!(tab instanceof HTMLElement)) return;

    const data = await this.#fetchJson('question_aggregate');
    const count = data?.questionAggregate?.approvedQuestionCount ?? 0;
    if (!count) return;

    const countLabel = tab.querySelector('.ts-okendo-reviews-modal__tab-count');
    if (countLabel) countLabel.textContent = `(${count})`;
    tab.hidden = false;
  }

  async #hydrateAggregate() {
    const data = await this.#fetchJson('review_aggregate');
    const aggregate = data?.reviewAggregate;
    if (!aggregate) return;

    const total = aggregate.reviewCount || aggregate.ratingAndReviewCount || 0;
    if (!total) return;

    const valuesTotal = aggregate.reviewRatingValuesTotal ?? aggregate.ratingAndReviewValuesTotal;
    if (!this.refs.average.textContent?.trim() && valuesTotal) {
      this.refs.average.textContent = (valuesTotal / total).toFixed(1);
    }

    const levels = aggregate.reviewCountByLevel ?? aggregate.ratingAndReviewCountByLevel ?? {};
    const counts = [5, 4, 3, 2, 1].map((level) => ({
      level,
      count: levels[`level${level}Count`] ?? 0,
    }));
    const max = Math.max(...counts.map(({ count }) => count));

    if (max > 0) {
      this.refs.distribution.replaceChildren(
        ...counts.map(({ level, count }) => this.#buildDistributionRow(level, count, max))
      );
      this.refs.distribution.hidden = false;
    }

    if (this.hasAttribute('data-hide-summary')) return;

    // Strictly positive: a 0 could mean the recommend question isn't
    // collected, and "0% recommend" should never render either way.
    if (aggregate.recommendationCount > 0) {
      const percent = Math.round((aggregate.recommendationCount / total) * 100);
      this.refs.consensus.textContent = (this.dataset.stringRecommend ?? '[percent]%').replace(
        '[percent]',
        String(percent)
      );
      this.refs.consensus.hidden = false;
      this.refs.summary.hidden = false;
    }

    this.#renderAttributeRatings(aggregate.attributeRatings);
  }

  /**
   * Renders attribute consensus lines (e.g. Sizing → "True to Size") from the
   * aggregate's `attributeRatings`. Only `centered-range` attributes carry the
   * min/mid/max labels this renders.
   *
   * @param {Array<{title?: string, type?: string, value?: number, minLabel?: string, midLabel?: string, maxLabel?: string}> | undefined} attributeRatings
   */
  #renderAttributeRatings(attributeRatings) {
    const rows = (attributeRatings ?? [])
      .filter((attribute) => attribute.type === 'centered-range' && attribute.title && attribute.value != null)
      .map((attribute) => {
        // Centered range: 0 is the midpoint; lean past ±⅓ toward an end label.
        const value = attribute.value ?? 0;
        const label = value <= -1 / 3 ? attribute.minLabel : value >= 1 / 3 ? attribute.maxLabel : attribute.midLabel;
        if (!label) return null;

        const row = document.createElement('li');
        row.className = 'ts-okendo-review-highlights__attribute';

        const title = document.createElement('span');
        title.className = 'ts-okendo-review-highlights__attribute-title';
        title.textContent = attribute.title ?? '';

        row.append(title, document.createTextNode(label));
        return row;
      })
      .filter((row) => row != null);

    if (!rows.length) return;

    this.refs.attributes.replaceChildren(...rows);
    this.refs.attributes.hidden = false;
    this.refs.summary.hidden = false;
  }

  /**
   * @param {number} level - Star level (1–5).
   * @param {number} count - Review count at that level.
   * @param {number} max - Highest per-level count (bars scale relative to it).
   * @returns {HTMLLIElement}
   */
  #buildDistributionRow(level, count, max) {
    const row = document.createElement('li');
    row.className = 'ts-okendo-review-highlights__distribution-row';

    const levelLabel = document.createElement('span');
    levelLabel.className = 'ts-okendo-review-highlights__distribution-level';
    levelLabel.setAttribute('aria-hidden', 'true');
    levelLabel.textContent = String(level);

    const bar = document.createElement('span');
    bar.className = 'ts-okendo-review-highlights__distribution-bar';
    bar.setAttribute('aria-hidden', 'true');
    const fill = document.createElement('span');
    fill.className = 'ts-okendo-review-highlights__distribution-fill';
    fill.style.setProperty('--ts-okendo-fill', `${Math.round((count / max) * 100)}%`);
    bar.append(fill);

    const countLabel = document.createElement('span');
    countLabel.className = 'ts-okendo-review-highlights__distribution-count';
    countLabel.setAttribute('aria-hidden', 'true');
    countLabel.textContent = String(count);

    const srLabel = document.createElement('span');
    srLabel.className = 'visually-hidden';
    srLabel.textContent = (this.dataset.stringRatingLabel ?? '[count] × [rating]')
      .replace('[count]', String(count))
      .replace('[rating]', String(level));

    row.append(levelLabel, bar, countLabel, srLabel);
    return row;
  }

  async #hydrateSummary() {
    const data = await this.#fetchJson('reviews_summary');
    const body = data?.reviewsSummary?.body;
    if (!body) return;

    this.refs.summaryBody.textContent = body;
    this.refs.summaryBody.hidden = false;
    this.refs.summary.hidden = false;
  }

  async #hydrateCards() {
    const limit = Number(this.dataset.reviewLimit) || 3;
    const orderBy = this.dataset.orderBy || 'helpful desc';
    const data = await this.#fetchJson(`reviews?limit=${limit}&orderBy=${encodeURIComponent(orderBy)}`);
    /** @type {Array<{title?: string, body?: string, reviewer?: {displayName?: string}}>} */
    const reviews = (data?.reviews ?? []).filter(
      (/** @type {{title?: string, body?: string}} */ review) => review.body || review.title
    );
    if (!reviews.length) return;

    // Mount the shell from the inert template with the slides already inside
    // (like ts-recently-viewed-carousel) — slideshow-component only sets up
    // its Scroller when slides exist at connect time.
    const template = this.refs.carouselTemplate;
    if (!(template instanceof HTMLTemplateElement)) return;
    const content = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    const scroller = content.querySelector('slideshow-slides');
    if (!scroller) return;

    scroller.append(...reviews.map((review, index) => this.#buildCard(review, index, reviews.length)));
    // The arrows ship disabled; with more than one review the next arrow is
    // operable from the start (ts-carousel-arrows re-asserts the state on
    // connect, this just avoids a disabled flash).
    if (reviews.length > 1) {
      content.querySelector('.ts-horiz-carousel__arrow--next')?.removeAttribute('disabled');
    }

    template.replaceWith(content);
    this.refs.cards.hidden = false;
    this.#setUpCardClamps();
  }

  /**
   * Opens the "all reviews" dialog, hydrating the reviews list on first open.
   */
  #openReviewsModal = () => {
    const dialog = this.querySelector('dialog-component');
    if (!(dialog instanceof DialogComponent)) return;

    this.#cloneOverview();
    dialog.showDialog();
    if (this.#modalList('reviews')?.dataset.loaded !== 'true') this.#loadModalPage('reviews');
  };

  /**
   * @param {string} kind - 'reviews' | 'questions'.
   * @returns {HTMLElement | null}
   */
  #modalList(kind) {
    const list = this.querySelector(
      `.ts-okendo-reviews-modal__panel[data-kind="${kind}"] .ts-okendo-reviews-modal__list`
    );
    return list instanceof HTMLElement ? list : null;
  }

  /**
   * Fills the modal's grey overview block with a clone of the (hydrated)
   * widget top: aggregate stats, distribution bars and AI summary. Cloning on
   * every open keeps it in sync without duplicating any hydration logic; refs
   * are stripped so the copies never register on a component, and the see-all
   * CTA is dropped (it would point at the modal the user is already in).
   */
  #cloneOverview() {
    const overview = this.querySelector('.ts-okendo-reviews-modal__overview');
    const top = this.querySelector('.ts-okendo-review-highlights__top');
    if (!(overview instanceof HTMLElement) || !top) return;

    const clone = /** @type {HTMLElement} */ (top.cloneNode(true));
    for (const element of clone.querySelectorAll('[ref]')) element.removeAttribute('ref');
    clone.querySelector('.ts-okendo-review-highlights__see-all')?.remove();
    overview.replaceChildren(clone);
  }

  /** @param {Event} event */
  #selectClickedTab = (event) => {
    const tab = event.currentTarget;
    if (tab instanceof HTMLElement && tab.dataset.tab) this.#selectModalTab(tab.dataset.tab);
  };

  /**
   * Roving-tabindex arrow-key support for the Reviews/Questions tablist.
   *
   * @param {KeyboardEvent} event
   */
  #handleTabKeydown = (event) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    const tabs = [...this.querySelectorAll('.ts-okendo-reviews-modal__tab:not([hidden])')];
    const index = tabs.findIndex((tab) => tab === document.activeElement);
    if (index === -1 || tabs.length < 2) return;

    event.preventDefault();
    const step = event.key === 'ArrowRight' ? 1 : tabs.length - 1;
    const next = tabs[(index + step) % tabs.length];
    if (!(next instanceof HTMLElement) || !next.dataset.tab) return;
    next.focus();
    this.#selectModalTab(next.dataset.tab);
  };

  /**
   * Shows one tab panel, lazy-loading its first page.
   *
   * @param {string} kind - 'reviews' | 'questions'.
   */
  #selectModalTab(kind) {
    for (const tab of this.querySelectorAll('.ts-okendo-reviews-modal__tab')) {
      const selected = tab instanceof HTMLElement && tab.dataset.tab === kind;
      tab.setAttribute('aria-selected', String(selected));
      tab.setAttribute('tabindex', selected ? '0' : '-1');
    }
    const incoming = this.#modalList(kind);
    // First switch into an unloaded panel: hold the outgoing list's height
    // while the first page loads so the dialog's scrollable content doesn't
    // collapse and clamp the scroll position. Measured before the panels
    // swap (a hidden list has no height); #loadModalPage releases it.
    if (incoming && incoming.dataset.loaded !== 'true') {
      const outgoing = this.querySelector(
        '.ts-okendo-reviews-modal__panel:not([hidden]) .ts-okendo-reviews-modal__list'
      );
      if (outgoing instanceof HTMLElement && outgoing !== incoming) {
        incoming.style.minHeight = `${outgoing.offsetHeight}px`;
      }
    }

    for (const region of this.querySelectorAll('.ts-okendo-reviews-modal__panel, .ts-okendo-reviews-modal__controls')) {
      if (region instanceof HTMLElement) region.hidden = region.dataset.kind !== kind;
    }
    if (incoming?.dataset.loaded !== 'true') this.#loadModalPage(kind);
  }

  /**
   * Restarts the panel's list under the newly chosen sort order. Bumping the
   * request token invalidates any in-flight page, and clearing aria-busy
   * lets the fresh load start immediately.
   *
   * @param {Event} event
   */
  #changeModalSort = (event) => {
    const panel = event.target instanceof Element && event.target.closest('[data-kind]');
    const kind = panel instanceof HTMLElement && panel.dataset.kind;
    if (!kind) return;
    const list = this.#modalList(kind);
    if (!list) return;
    // Freeze the list's height while it reloads: emptying it would shrink the
    // dialog's scrollable content and clamp the scroll position upward.
    // #loadModalPage releases it once the reload lands.
    list.style.minHeight = `${list.offsetHeight}px`;
    list.dataset.requestToken = String(Number(list.dataset.requestToken ?? '0') + 1);
    delete list.dataset.loaded;
    delete list.dataset.nextUrl;
    list.replaceChildren();
    list.setAttribute('aria-busy', 'false');
    this.#loadModalPage(kind);
  };

  /** @param {Event} event */
  #loadNextModalPage = (event) => {
    const panel = event.currentTarget instanceof Element && event.currentTarget.closest('[data-kind]');
    if (panel instanceof HTMLElement && panel.dataset.kind) this.#loadModalPage(panel.dataset.kind);
  };

  /** @param {string} kind */
  #modalFirstPageUrl(kind) {
    const select = this.querySelector(`[data-kind="${kind}"] .ts-okendo-reviews-modal__sort-select`);
    const fallback = kind === 'questions' ? 'date desc' : 'rating desc';
    const orderBy = select instanceof HTMLSelectElement ? select.value : fallback;
    const path = kind === 'questions' ? 'questions' : 'reviews';
    // Okendo accepts limit 1–25; the section setting stays in that range.
    const limit = Math.min(25, Math.max(1, Number(this.dataset.modalPageSize) || 25));
    return `${path}?limit=${limit}&orderBy=${encodeURIComponent(orderBy)}`;
  }

  /**
   * Fetches one panel's next page (first page when nothing is loaded yet)
   * and appends the items to its list. The API pages via `nextUrl`.
   *
   * All pagination state (loaded flag, cursor, request token) lives in data
   * attributes on the list element, not instance fields: section
   * morphs/clones replace the component instance but keep the populated
   * DOM, so the state must travel with the DOM it describes — a fresh
   * instance with blank fields would re-append page 1 under the old rows.
   *
   * @param {string} kind - 'reviews' | 'questions'.
   */
  async #loadModalPage(kind) {
    const list = this.#modalList(kind);
    if (!list || list.getAttribute('aria-busy') === 'true') return;
    const panel = list.closest('.ts-okendo-reviews-modal__panel');
    const more = panel?.querySelector('.ts-okendo-reviews-modal__more');

    const firstPage = list.dataset.loaded !== 'true';
    const nextUrl = list.dataset.nextUrl;
    if (!firstPage && !nextUrl) {
      // Everything is loaded — a stale Show more (cloned mid-flight) just hides.
      if (more instanceof HTMLElement) more.hidden = true;
      return;
    }

    const token = String(Number(list.dataset.requestToken ?? '0') + 1);
    list.dataset.requestToken = token;
    list.setAttribute('aria-busy', 'true');
    // Skeleton cards mark the loading page — appended below existing rows
    // on Show more, alone in the list on a first page.
    list.append(...this.#buildSkeletons());
    if (more instanceof HTMLElement) more.hidden = true;

    const data = await this.#fetchJson(nextUrl ?? this.#modalFirstPageUrl(kind));
    // A sort change bumps the token and clears the list mid-flight; drop
    // this response instead of appending stale items.
    if (list.dataset.requestToken !== token) return;

    // Covers the empty/failed response too — replaceChildren below only
    // runs when items actually arrived.
    for (const skeleton of list.querySelectorAll('.ts-okendo-reviews-modal__skeleton')) skeleton.remove();

    const items =
      kind === 'questions'
        ? (data?.questions ?? [])
            .filter((/** @type {OkendoQuestion} */ question) => question.body)
            .map((/** @type {OkendoQuestion} */ question) => this.#buildModalQuestion(question))
        : (data?.reviews ?? [])
            .filter((/** @type {OkendoReview} */ review) => review.body || review.title)
            .map((/** @type {OkendoReview} */ review) => this.#buildModalReview(review));
    if (items.length) {
      // Belt and suspenders: a first page always REPLACES whatever is shown.
      if (firstPage) list.replaceChildren();
      list.append(...items);
      list.dataset.loaded = 'true';
    }

    if (data?.nextUrl) {
      list.dataset.nextUrl = data.nextUrl;
    } else if (data) {
      // Successful response without a cursor — the list is complete. (A
      // failed fetch keeps the cursor so Show more can retry.)
      delete list.dataset.nextUrl;
    }
    if (more instanceof HTMLElement) more.hidden = !list.dataset.nextUrl;
    list.setAttribute('aria-busy', 'false');
    list.style.minHeight = '';
  }

  /**
   * Placeholder cards shown in a panel's list while its first page loads.
   * Decorative only — aria-busy on the list carries the loading semantics.
   *
   * @param {number} [count]
   * @returns {HTMLElement[]}
   */
  #buildSkeletons(count = 3) {
    const bars = ['sm', 'md', '', '', 'lg'];
    return Array.from({ length: count }, () => {
      const item = document.createElement('div');
      item.className = 'ts-okendo-reviews-modal__item ts-okendo-reviews-modal__skeleton';
      item.setAttribute('aria-hidden', 'true');
      item.append(
        ...bars.map((size) => {
          const bar = document.createElement('div');
          bar.className = `ts-okendo-reviews-modal__skeleton-bar${
            size ? ` ts-okendo-reviews-modal__skeleton-bar--${size}` : ''
          }`;
          return bar;
        })
      );
      return item;
    });
  }

  /**
   * One review row, mirroring Okendo's own widget: author + verified badge +
   * relative date, options purchased, star rating, title, full body, media
   * thumbnails, per-review sizing scale and helpful/unhelpful counts.
   *
   * @param {OkendoReview} review
   * @returns {HTMLElement}
   */
  #buildModalReview(review) {
    const item = document.createElement('article');
    // The --review wrappers are display: contents on mobile (flat stack) and
    // become the desktop card's who/content columns; date and votes make the
    // third column via absolute pinning / align-self.
    item.className = 'ts-okendo-reviews-modal__item ts-okendo-reviews-modal__item--review';

    const aside = document.createElement('div');
    aside.className = 'ts-okendo-reviews-modal__item-aside';
    aside.append(this.#buildItemHeader(review.reviewer?.displayName, review.reviewer?.isVerified, review.dateCreated));

    const purchased = this.#buildPurchasedLine(review);
    if (purchased) aside.append(purchased);

    const profile = this.#buildReviewerLine(review);
    if (profile) aside.append(profile);
    item.append(aside);

    const main = document.createElement('div');
    main.className = 'ts-okendo-reviews-modal__item-main';
    item.append(main);

    if (review.rating) main.append(this.#buildStars(review.rating));

    if (review.title) {
      const title = document.createElement('h3');
      title.className = 'ts-okendo-reviews-modal__item-title';
      title.textContent = review.title;
      main.append(title);
    }

    if (review.body) {
      const body = document.createElement('p');
      body.className = 'ts-okendo-reviews-modal__item-body';
      body.textContent = review.body;
      main.append(body);
    }

    const images = (review.media ?? []).filter((media) => media.type === 'image' && media.thumbnailUrl);
    if (review.productImageUrl || images.length) {
      const row = document.createElement('div');
      row.className = 'ts-okendo-reviews-modal__item-media';

      // The reviewed product's thumbnail — reviews are grouped across sibling
      // products, so this shows which one was reviewed (the OOB widget's
      // oke-product-thumbnail), linked to its PDP when the API provides one.
      if (review.productImageUrl) {
        const image = document.createElement('img');
        image.src = review.productImageUrl;
        image.alt = review.productName ?? '';
        image.loading = 'lazy';
        if (review.productUrl) {
          const link = document.createElement('a');
          link.className = 'ts-okendo-reviews-modal__item-media-link';
          link.href = review.productUrl;
          link.append(image);
          row.append(link);
        } else {
          row.append(image);
        }
      }

      row.append(
        ...images.map((media) => {
          const image = document.createElement('img');
          image.src = /** @type {string} */ (media.thumbnailUrl);
          image.alt = media.alt ?? '';
          image.loading = 'lazy';
          image.width = 120;
          image.height = 120;
          return image;
        })
      );
      main.append(row);
    }

    const sizing = (review.attributesWithRating ?? []).find(
      (attribute) => attribute.type === 'centered-range' && attribute.minLabel && attribute.maxLabel
    );
    if (sizing && typeof sizing.value === 'number') main.append(this.#buildScale(sizing));

    // Desktop shows the date in the third column, so it gets its own copy at
    // this point in the DOM — reading/magnifier order then tracks each
    // breakpoint's visual order (the header copy is mobile-only, CSS shows
    // exactly one of the two).
    const headerDate = aside.querySelector('.ts-okendo-reviews-modal__item-date');
    if (headerDate) {
      const columnDate = /** @type {HTMLElement} */ (headerDate.cloneNode(true));
      columnDate.classList.add('ts-okendo-reviews-modal__item-date--column');
      item.append(columnDate);
    }

    if (review.helpfulCount != null || review.unhelpfulCount != null) {
      item.append(
        this.#buildVotes({
          itemId: review.reviewId,
          contentType: 'review',
          path: `reviews/${review.reviewId}/vote`,
          helpfulCount: review.helpfulCount,
          unhelpfulCount: review.unhelpfulCount,
        })
      );
    }

    return item;
  }

  /**
   * One question row — author + date, body, "I had this question" votes —
   * with its public approved answers as grey cards (author, body, "Was this
   * helpful?" votes).
   *
   * @param {OkendoQuestion} question
   * @returns {HTMLElement}
   */
  #buildModalQuestion(question) {
    const item = document.createElement('article');
    item.className = 'ts-okendo-reviews-modal__item';

    item.append(this.#buildItemHeader(question.author?.displayName, false, question.dateCreated));

    const body = document.createElement('p');
    body.className = 'ts-okendo-reviews-modal__item-body';
    body.textContent = question.body ?? '';
    item.append(body);

    if (question.questionId) {
      item.append(
        this.#buildVotes({
          itemId: question.questionId,
          contentType: 'question',
          path: `questions/${question.questionId}/vote`,
          helpfulCount: question.helpfulCount,
          unhelpfulCount: question.unhelpfulCount,
          label: this.dataset.stringHadQuestion,
        })
      );
    }

    for (const answer of question.answers ?? []) {
      if (answer.isPrivate || answer.status !== 'approved' || !answer.body) continue;

      const wrapper = document.createElement('div');
      wrapper.className = 'ts-okendo-reviews-modal__answer';
      // The design shows the answerer's name without a date.
      wrapper.append(this.#buildItemHeader(answer.author?.displayName));

      const answerBody = document.createElement('p');
      answerBody.className = 'ts-okendo-reviews-modal__item-body';
      answerBody.textContent = this.#htmlToText(answer.body);
      wrapper.append(answerBody);

      if (question.questionId && answer.answerId) {
        wrapper.append(
          this.#buildVotes({
            itemId: answer.answerId,
            contentType: 'answer',
            path: `questions/${question.questionId}/answers/${answer.answerId}/vote`,
            helpfulCount: answer.helpfulCount,
            unhelpfulCount: answer.unhelpfulCount,
            label: this.dataset.stringWasHelpful,
          })
        );
      }
      item.append(wrapper);
    }

    return item;
  }

  /**
   * Author + optional verified badge on the left, relative date on the right.
   *
   * @param {string} [name]
   * @param {boolean} [isVerified]
   * @param {string} [dateCreated]
   * @returns {HTMLElement}
   */
  #buildItemHeader(name, isVerified, dateCreated) {
    const header = document.createElement('header');
    header.className = 'ts-okendo-reviews-modal__item-header';

    if (name) {
      const author = document.createElement('span');
      author.className = 'ts-okendo-reviews-modal__item-author';
      author.textContent = name;
      header.append(author);
    }

    if (isVerified) {
      const verified = document.createElement('span');
      verified.className = 'ts-okendo-reviews-modal__item-verified';
      if (this.refs.verifiedTemplate instanceof HTMLTemplateElement) {
        verified.append(this.refs.verifiedTemplate.content.cloneNode(true));
      }
      verified.append(this.dataset.stringVerified ?? 'Verified Buyer');
      header.append(verified);
    }

    const relative = dateCreated ? this.#relativeDate(dateCreated) : '';
    if (dateCreated && relative) {
      const date = document.createElement('time');
      date.className = 'ts-okendo-reviews-modal__item-date';
      date.dateTime = dateCreated;
      date.textContent = relative;
      header.append(date);
    }

    return header;
  }

  /**
   * "Size purchased: M · Color purchased: Blue" — the variant name's ` / `
   * segments zipped with the product's option names (grouped sibling
   * products share the option schema). Only Size and Color render; other
   * options (Style, internal codes) are noise here. Some reviews carry no
   * variant name at all — those fall back to the capture form's labeled
   * `productAttributes` ("Size Purchased").
   *
   * @param {OkendoReview} review
   * @returns {HTMLElement | null}
   */
  #buildPurchasedLine(review) {
    const variantName = review.productVariantName;

    /** @type {string[]} */
    let names = [];
    try {
      names = JSON.parse(this.dataset.optionNames ?? '[]');
    } catch {
      names = [];
    }

    const values = variantName && variantName !== 'Default Title' ? variantName.split(' / ') : [];
    const template = this.dataset.stringOptionPurchased ?? '[option]: [value]';
    let parts = names
      .map((name, index) => ({ name, value: values[index] }))
      .filter((pair) => pair.value && /^(size|color|colour)$/i.test(pair.name ?? ''))
      .map((pair) => template.replace('[option]', pair.name).replace('[value]', /** @type {string} */ (pair.value)));

    if (!parts.length) {
      parts = (review.productAttributes ?? [])
        .map((attribute) => ({
          title: attribute.title,
          // multi-value attributes carry an array; drop-downs a string.
          value: Array.isArray(attribute.value) ? attribute.value.join(', ') : attribute.value,
        }))
        .filter((attribute) => attribute.title && attribute.value)
        .map((attribute) => `${attribute.title}: ${attribute.value}`);
    }
    if (!parts.length) return null;

    const line = document.createElement('p');
    line.className = 'ts-okendo-reviews-modal__item-meta';
    line.append(
      ...parts.map((text) => {
        const part = document.createElement('span');
        part.textContent = text;
        return part;
      })
    );
    return line;
  }

  /**
   * Second meta row — reviewer profile answers (age, height, usual size)
   * when the capture form collected them (only a fraction of TS reviews
   * have these). Titles arrive as the full question text ("How old are
   * you?") and render verbatim, exactly like the OOB widget.
   *
   * @param {OkendoReview} review
   * @returns {HTMLElement | null}
   */
  #buildReviewerLine(review) {
    const attributes = (review.reviewer?.attributes ?? []).filter((attribute) => attribute.title && attribute.value);
    if (!attributes.length) return null;

    const line = document.createElement('p');
    line.className = 'ts-okendo-reviews-modal__item-meta';
    line.append(
      ...attributes.map((attribute) => {
        const part = document.createElement('span');
        part.textContent = `${attribute.title} ${attribute.value}`;
        return part;
      })
    );
    return line;
  }

  /**
   * Five stars, the first `rating` filled.
   *
   * @param {number} rating
   * @returns {HTMLElement}
   */
  #buildStars(rating) {
    const stars = document.createElement('div');
    stars.className = 'ts-okendo-reviews-modal__item-stars';
    stars.setAttribute('role', 'img');
    stars.setAttribute(
      'aria-label',
      (this.dataset.stringReviewRating ?? '[rating]/5').replace('[rating]', String(rating))
    );

    const template = this.refs.starTemplate;
    for (let level = 1; level <= 5; level += 1) {
      const star = document.createElement('span');
      star.className = 'ts-okendo-reviews-modal__star';
      if (level > rating) star.classList.add('ts-okendo-reviews-modal__star--empty');
      if (template instanceof HTMLTemplateElement) star.append(template.content.cloneNode(true));
      stars.append(star);
    }

    return stars;
  }

  /**
   * Per-review sizing scale: a track with a dot at the review's value
   * (-1…1 → 0…100%) over the min/mid/max labels.
   *
   * @param {OkendoAttributeRating} attribute
   * @returns {HTMLElement}
   */
  #buildScale(attribute) {
    const value = Math.min(1, Math.max(-1, attribute.value ?? 0));
    const lean = value <= -1 / 3 ? attribute.minLabel : value >= 1 / 3 ? attribute.maxLabel : attribute.midLabel;

    const scale = document.createElement('div');
    scale.className = 'ts-okendo-reviews-modal__item-scale';
    scale.setAttribute('role', 'img');
    scale.setAttribute('aria-label', [attribute.title, lean].filter(Boolean).join(': '));

    const track = document.createElement('span');
    track.className = 'ts-okendo-reviews-modal__item-scale-track';
    const dot = document.createElement('span');
    dot.className = 'ts-okendo-reviews-modal__item-scale-dot';
    dot.style.setProperty('--ts-okendo-scale-pos', `${Math.round(((value + 1) / 2) * 100)}%`);
    track.append(dot);

    const labels = document.createElement('span');
    labels.className = 'ts-okendo-reviews-modal__item-scale-labels';
    labels.append(
      ...[attribute.minLabel, attribute.midLabel, attribute.maxLabel].map((label) => {
        const span = document.createElement('span');
        span.textContent = label ?? '';
        return span;
      })
    );

    scale.append(track, labels);
    return scale;
  }

  /**
   * Interactive helpful/unhelpful votes for a review, question or answer,
   * speaking the same (undocumented but CORS-open) protocol as Okendo's
   * widget: POST the item's vote endpoint with `{voteType}` — `upvote`/
   * `downvote`, `-cancel` to undo, `swap-to-` to flip — and the visitor's
   * vote remembered in the widget's own localStorage record. Votes apply
   * optimistically, then reconcile with the response's authoritative counts
   * (or revert if the request fails).
   *
   * @param {object} options
   * @param {string} [options.itemId] - Okendo id, also the vote-store key.
   * @param {'review' | 'question' | 'answer'} options.contentType - Vote-store record type (mirrors the OOB widget's).
   * @param {string} options.path - Vote endpoint path relative to the store base.
   * @param {number} [options.helpfulCount]
   * @param {number} [options.unhelpfulCount]
   * @param {string} [options.label] - Visible text rendered before the buttons.
   * @returns {HTMLElement}
   */
  #buildVotes({ itemId, contentType, path, helpfulCount, unhelpfulCount, label }) {
    const votes = document.createElement('div');
    votes.className = 'ts-okendo-reviews-modal__item-votes';

    if (label) {
      const text = document.createElement('span');
      text.className = 'ts-okendo-reviews-modal__item-votes-label';
      text.textContent = label;
      votes.append(text);
    }

    const directions = /** @type {Array<'upvote' | 'downvote'>} */ (['upvote', 'downvote']);
    const controls = directions.map((direction) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ts-okendo-reviews-modal__item-vote';
      if (direction === 'downvote') button.classList.add('ts-okendo-reviews-modal__item-vote--down');
      // Both icon variants ship in the button; aria-pressed picks one in CSS.
      const icons = [
        { template: this.refs.likeTemplate, variant: 'outline' },
        { template: this.refs.likeFilledTemplate, variant: 'filled' },
      ];
      for (const { template, variant } of icons) {
        if (!(template instanceof HTMLTemplateElement)) continue;
        const icon = document.createElement('span');
        icon.className = `ts-okendo-reviews-modal__item-vote-icon ts-okendo-reviews-modal__item-vote-icon--${variant}`;
        icon.append(template.content.cloneNode(true));
        button.append(icon);
      }
      const count = document.createElement('span');
      button.append(count);
      votes.append(button);
      return { direction, button, count };
    });

    /**
     * @param {number} helpful
     * @param {number} unhelpful
     */
    const sync = (helpful, unhelpful) => {
      const stored = this.#storedVote(itemId, contentType);
      for (const { direction, button, count } of controls) {
        const total = direction === 'upvote' ? helpful : unhelpful;
        count.textContent = String(total);
        button.setAttribute('aria-pressed', String(stored === direction));
        const template = direction === 'upvote' ? this.dataset.stringHelpful : this.dataset.stringUnhelpful;
        button.setAttribute('aria-label', (template ?? `[count] ${direction}`).replace('[count]', String(total)));
      }
    };
    // Local count state for optimistic updates; the server response
    // reconciles it (or a failure reverts it).
    let counts = { helpful: helpfulCount ?? 0, unhelpful: unhelpfulCount ?? 0 };
    sync(counts.helpful, counts.unhelpful);

    // Requests run sequentially so rapid clicks never get dropped: each
    // click's verb comes from the optimistic local state, so replaying them
    // in order reproduces the intent chain server-side.
    let chain = Promise.resolve();
    let pending = 0;

    for (const { direction, button } of controls) {
      button.addEventListener('click', () => {
        if (!itemId) return;
        const current = this.#storedVote(itemId, contentType);
        const voteType = current === direction ? `${direction}-cancel` : current ? `swap-to-${direction}` : direction;
        const next = current === direction ? '' : direction;

        // Optimistic: apply the delta and remember the vote immediately.
        const previous = { ...counts, vote: current };
        if (current === 'upvote') counts.helpful -= 1;
        if (current === 'downvote') counts.unhelpful -= 1;
        if (next === 'upvote') counts.helpful += 1;
        if (next === 'downvote') counts.unhelpful += 1;
        counts.helpful = Math.max(0, counts.helpful);
        counts.unhelpful = Math.max(0, counts.unhelpful);
        this.#setStoredVote(itemId, contentType, next);
        sync(counts.helpful, counts.unhelpful);

        pending += 1;
        chain = chain.then(async () => {
          const data = await this.#postJson(path, { voteType });
          pending -= 1;
          const updated = data?.review ?? data?.question ?? data?.answer;
          // A newer click's optimistic state is showing — let its own
          // response (or revert) settle the display.
          if (pending > 0) return;

          if (!updated) {
            // Revert the optimistic update — the vote didn't land.
            counts = { helpful: previous.helpful, unhelpful: previous.unhelpful };
            this.#setStoredVote(itemId, contentType, previous.vote);
            sync(counts.helpful, counts.unhelpful);
            return;
          }

          // Reconcile with the server's authoritative counts.
          counts = { helpful: updated.helpfulCount ?? 0, unhelpful: updated.unhelpfulCount ?? 0 };
          sync(counts.helpful, counts.unhelpful);
        });
      });
    }

    return votes;
  }

  /** @returns {{storedVotes?: Array<{itemId?: string, contentType?: string, voteType?: string}>}} */
  #readVoteStore() {
    try {
      const raw = localStorage.getItem(VOTE_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  /**
   * @param {string} [itemId]
   * @param {string} [contentType] - `'review'`, `'question'` or `'answer'`.
   * @returns {string} `''`, `'upvote'` or `'downvote'`.
   */
  #storedVote(itemId, contentType) {
    if (!itemId) return '';
    const vote = (this.#readVoteStore().storedVotes ?? []).find(
      (entry) => entry.itemId === itemId && entry.contentType === contentType
    );
    return vote?.voteType ?? '';
  }

  /**
   * @param {string} itemId
   * @param {string} contentType - `'review'`, `'question'` or `'answer'`.
   * @param {string} voteType - `''` clears the record.
   */
  #setStoredVote(itemId, contentType, voteType) {
    const store = this.#readVoteStore();
    const storedVotes = (store.storedVotes ?? []).filter(
      (entry) => !(entry.itemId === itemId && entry.contentType === contentType)
    );
    if (voteType) storedVotes.push({ itemId, contentType, voteType });
    try {
      localStorage.setItem(VOTE_STORAGE_KEY, JSON.stringify({ ...store, storedVotes }));
    } catch {
      // Storage unavailable (private mode etc.) — the vote still lands, it
      // just isn't remembered across reloads.
    }
  }

  /**
   * @param {string} path - Endpoint path appended to the store base
   *   (`/stores/{subscriberId}/…`).
   * @param {object} body
   * @returns {Promise<any | null>} Parsed JSON, or null when unavailable.
   */
  async #postJson(path, body) {
    const base = this.#storeBase;
    if (!base) return null;

    try {
      const response = await fetch(`${base}/${path}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
        signal: this.#abortController?.signal,
      });
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }

  /**
   * "2 weeks ago"-style relative date in the page language.
   *
   * @param {string} iso
   * @returns {string}
   */
  #relativeDate(iso) {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';

    this.#relativeTimeFormat ??= new Intl.RelativeTimeFormat(document.documentElement.lang || 'en', {
      numeric: 'auto',
    });

    /** @type {Array<[number, Intl.RelativeTimeFormatUnit]>} */
    const divisions = [
      [60, 'second'],
      [60, 'minute'],
      [24, 'hour'],
      [7, 'day'],
      [4.34524, 'week'],
      [12, 'month'],
      [Infinity, 'year'],
    ];
    let duration = (date.getTime() - Date.now()) / 1000;
    for (const [amount, unit] of divisions) {
      if (Math.abs(duration) < amount) return this.#relativeTimeFormat.format(Math.round(duration), unit);
      duration /= amount;
    }
    return '';
  }

  /**
   * Flattens Okendo's HTML answer bodies (store-authored) to plain text with
   * paragraph breaks; rendered with `white-space: pre-line`.
   *
   * @param {string} html
   * @returns {string}
   */
  #htmlToText(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const blocks = [...doc.body.querySelectorAll('p, li')]
      .map((element) => element.textContent?.trim())
      .filter(Boolean);
    return blocks.length ? blocks.join('\n') : (doc.body.textContent?.trim() ?? '');
  }

  /**
   * @param {{title?: string, body?: string, reviewer?: {displayName?: string}}} review
   * @param {number} index - Zero-based slide index.
   * @param {number} length - Total slide count.
   * @returns {HTMLDivElement}
   */
  #buildCard(review, index, length) {
    const card = document.createElement('div');
    card.className = 'ts-okendo-review-highlights__card';
    // slideshow-component only tracks elements carrying ref="slides[]" —
    // without it the arrows are inert (see snippets/ts-carousel-product-card.liquid).
    card.setAttribute('ref', 'slides[]');
    card.setAttribute('role', 'group');
    card.setAttribute('aria-roledescription', 'slide');
    card.setAttribute(
      'aria-label',
      (this.dataset.stringSlideLabel ?? '[index] / [length]')
        .replace('[index]', String(index + 1))
        .replace('[length]', String(length))
    );

    // Title row — the stationary arrows overlay its end; CSS gives the row
    // the arrows' height/width so the card's 16px gap applies below them.
    // A titleless card still needs the row when the arrows show (length > 1),
    // but skipping it otherwise avoids an empty row plus a stray flex gap.
    if (review.title || length > 1) {
      const header = document.createElement('div');
      header.className = 'ts-okendo-review-highlights__card-header';

      if (review.title) {
        const title = document.createElement('h3');
        title.className = 'ts-okendo-review-highlights__card-title';
        title.textContent = review.title;
        header.append(title);
      }

      card.append(header);
    }

    if (review.body) {
      const body = document.createElement('p');
      body.className = 'ts-okendo-review-highlights__card-body';
      const text = document.createElement('span');
      text.className = 'ts-okendo-review-highlights__card-text';
      text.textContent = review.body;
      body.append(text, this.#buildToggle(body, text, review.body));
      card.append(body);
    }

    const displayName = review.reviewer?.displayName;
    if (displayName) {
      const author = document.createElement('p');
      author.className = 'ts-okendo-review-highlights__card-author';
      author.textContent = `– ${displayName}`;
      card.append(author);
    }

    return card;
  }

  /**
   * Inline "Read more / Read less" toggle, ported from the PDP description's
   * expand-toggle mode (assets/ts-product-description.js). The toggle stays a
   * sibling of the text span, so rewriting the span never destroys it.
   *
   * @param {HTMLParagraphElement} body - The clamp container.
   * @param {HTMLSpanElement} text - Span holding the review text.
   * @param {string} full - The untrimmed review body.
   * @returns {HTMLButtonElement}
   */
  #buildToggle(body, text, full) {
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'ts-okendo-review-highlights__card-toggle';
    toggle.setAttribute('aria-expanded', 'false');
    toggle.textContent = this.dataset.stringReadMore ?? 'Read more';

    /** @type {CardClamp} */
    const clamp = { body, text, toggle, full, lastWidth: 0 };
    this.#cardClamps.push(clamp);

    toggle.addEventListener('click', () => {
      const expanded = body.dataset.state === 'expanded';
      // Label swaps BEFORE the re-trim: the trim fits the toggle's current
      // text, and "Read more…" is wider than "Read less" — trimming first
      // left the swapped-in label clipped below the fold.
      toggle.setAttribute('aria-expanded', String(!expanded));
      toggle.textContent = expanded
        ? (this.dataset.stringReadMore ?? 'Read more')
        : (this.dataset.stringReadLess ?? 'Read less');
      if (expanded) {
        delete body.dataset.state;
        this.#truncateCardBody(clamp);
      } else {
        body.dataset.state = 'expanded';
        text.textContent = full;
      }
    });

    return toggle;
  }

  /**
   * Trim each card to its clamp with the toggle inline, and keep the trim
   * correct across width changes and font swaps.
   */
  #setUpCardClamps() {
    this.#cardResizeObserver?.disconnect();
    this.#cardClamps = this.#cardClamps.filter((clamp) => clamp.body.isConnected);
    if (!this.#cardClamps.length) return;

    this.#cardResizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const clamp = this.#cardClamps.find((candidate) => candidate.body === entry.target);
        if (!clamp) continue;
        // Height changes are our own trimming and would loop.
        const width = entry.contentRect.width;
        if (Math.abs(width - clamp.lastWidth) < 1) continue;
        clamp.lastWidth = width;
        this.#pendingClamps.add(clamp);
      }
      if (!this.#pendingClamps.size) return;
      // Debounce until the resize settles: each re-trim binary-searches with
      // a forced layout per step, far too heavy per resize frame (the CSS
      // clamp keeps mid-drag overflow clipped meanwhile).
      clearTimeout(this.#retrimTimer);
      this.#retrimTimer = setTimeout(() => {
        for (const clamp of this.#pendingClamps) this.#truncateCardBody(clamp);
        this.#pendingClamps.clear();
      }, 150);
    });

    for (const clamp of this.#cardClamps) {
      clamp.lastWidth = clamp.body.clientWidth;
      this.#truncateCardBody(clamp);
      this.#cardResizeObserver.observe(clamp.body);
    }

    // Wrap points shift once custom fonts finish loading.
    document.fonts?.ready.then(() => {
      for (const clamp of this.#cardClamps) this.#truncateCardBody(clamp);
    });
  }

  /**
   * Show the toggle only when the full text overflows the clamp, then trim
   * the text so "… Read more" fits inline at the end of the last line.
   *
   * @param {CardClamp} clamp
   */
  #truncateCardBody(clamp) {
    const { body, text, full } = clamp;
    if (body.dataset.state === 'expanded') return;

    text.textContent = full;
    body.removeAttribute('data-truncated');
    if (!this.#overflows(body)) return;

    body.setAttribute('data-truncated', '');
    // Binary-search the largest character budget that still fits with the
    // inline toggle in place.
    let low = 0;
    let high = full.length;
    while (low < high) {
      const mid = Math.ceil((low + high) / 2);
      text.textContent = this.#clip(full, mid);
      if (this.#overflows(body)) {
        high = mid - 1;
      } else {
        low = mid;
      }
    }
    text.textContent = this.#clip(full, low);
  }

  /** @param {HTMLElement} element */
  #overflows(element) {
    return element.scrollHeight > element.clientHeight + 1;
  }

  /**
   * Cut to `budget` characters backed off to a word boundary, drop trailing
   * punctuation, and append an ellipsis.
   *
   * @param {string} full
   * @param {number} budget
   */
  #clip(full, budget) {
    const cut = full
      .slice(0, budget)
      .replace(/\s+\S*$/, '')
      .replace(/[\s.,;:]+$/, '');
    return `${cut}…`;
  }
}

if (!customElements.get('ts-okendo-review-highlights')) {
  customElements.define('ts-okendo-review-highlights', TSOkendoReviewHighlights);
}
